// ── ScheduledPmtLinesPanel ──
// Line items under ONE category (Other or Pass-through) of a scheduled payment.
//
// The payment's total for the category stays the number everything reads.
// Lines are optional detail:
//   - no lines  → the category is a single amount, exactly as before. "Split
//                 into line items" seeds one line with the current type/amount.
//   - lines     → the total is their sum (the API sets it). The parent form
//                 locks its own Type/Amount fields for the category.
//
// Removing every line and saving removes the breakdown: the category goes back
// to a single amount of $0 (non-zero → needs the confirm box, like any change).
//
// Saving requires the lines to tie out to the current total. To change a
// non-zero total on purpose, tick the confirm box (sent as allowTotalChange).
// A $0 total has nothing to protect: the lines just set it, no box needed.
//
// System lines (issystem — the calculated Unused Fee) are shown read-only.
import { useState } from "react";
import { B } from "../../../shared/theme";
import { fmt } from "../../../shared/format";
import { Btn, FInput, FInputNum, FSelect, Td, TdNum, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

const r2 = n => Math.round((Number(String(n ?? "").replace(/,/g, "")) || 0) * 100) / 100;

export const ScheduledPmtLinesPanel = ({ scheduledpmtid, category, title, lines, headerType, headerAmount,
                                         typeOptions, readOnly, onSaved }) => {
  const { api, role, audit } = useApp();
  const [editing, setEditing] = useState(null);     // array of draft lines while editing
  const [confirmTotal, setConfirmTotal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const canEdit = !readOnly && role.canEdit;
  const header = r2(headerAmount);
  const hasLines = lines.length > 0;

  const opts = (cur) => {
    const base = typeOptions.filter(o => o !== "Multiple (See Notes)");
    return cur && !base.includes(cur) ? [...base, cur] : base;
  };

  const startEdit = () => {
    setErr(""); setConfirmTotal(false);
    if (hasLines) {
      setEditing(lines.map(l => ({ ...l, amount: l.amount == null ? "" : String(l.amount) })));
    } else {
      // Seed from the single amount. "Multiple (See Notes)" isn't a real type,
      // so it starts blank and you pick what each piece is.
      const t = headerType && headerType !== "Multiple (See Notes)" ? headerType : "";
      setEditing([{ linetype: t, vendorname: "", invoicenumber: "", workdescription: "",
                    amount: header ? String(header) : "" }]);
    }
  };

  const upd = (i, k) => v => setEditing(rows => rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const addRow = () => setEditing(rows => [...rows, { linetype: "", vendorname: "", invoicenumber: "",
                                                      workdescription: "", amount: "" }]);
  const delRow = i => setEditing(rows => rows.filter((_, j) => j !== i));

  const draftTotal = editing ? r2(editing.reduce((s, l) => s + r2(l.amount), 0)) : 0;
  const diff = editing ? r2(draftTotal - header) : 0;
  const needsConfirm = diff !== 0 && header !== 0;

  const save = async () => {
    setErr("");
    if (editing.some(l => !String(l.linetype || "").trim())) { setErr("Every line needs a type."); return; }
    if (needsConfirm && !confirmTotal) { setErr("Lines don't tie out to the scheduled total. Fix them, or tick the box to change the total."); return; }
    setBusy(true);
    try {
      const res = await api.post("saveScheduledPmtLines", {
        scheduledpmtid, category, allowTotalChange: needsConfirm && confirmTotal,
        lines: editing.map(l => ({ id: l.id, linetype: l.linetype, vendorname: l.vendorname,
                                   invoicenumber: l.invoicenumber, workdescription: l.workdescription,
                                   amount: r2(l.amount), issystem: !!l.issystem })),
      });
      try {
        await audit("Edited", "ScheduledPmts", scheduledpmtid,
          (editing.length === 0 ? `${title} line items removed (total ${draftTotal.toFixed(2)}` :
          `${title} line items saved (${editing.length} line${editing.length === 1 ? "" : "s"}, total ${draftTotal.toFixed(2)}`)
          + (diff !== 0 ? `, changed from ${header.toFixed(2)})` : ")"));
      } catch (_) { /* audit is best-effort */ }
      setEditing(null);
      onSaved && onSaved(res && res.header);
    } catch (e) {
      setErr(e.message || "Could not save line items.");
    } finally { setBusy(false); }
  };

  const label = { fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em" };

  return (
    <div style={{ gridColumn:"1/-1", margin:"4px 0 12px", padding:"10px 12px", border:"1px solid "+B.cyanLight, borderRadius:6 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
        <div style={label}>{title} line items</div>
        {canEdit && !editing && (
          <Btn onClick={startEdit}>{hasLines ? "Edit line items" : "Split into line items"}</Btn>
        )}
      </div>

      {/* ── View ── */}
      {!editing && (!hasLines
        ? <div style={{ fontSize:12, color:"var(--color-text-secondary)" }}>
            Single amount{headerType ? ` — ${headerType}` : ""}{header ? ` · ${fmt(header)}` : ""}. No line items.
          </div>
        : <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr><Th>Type</Th><Th>Vendor</Th><Th>Invoice #</Th><Th>Type of work</Th><Th style={{ textAlign:"right" }}>Amount</Th></tr></thead>
            <tbody>
              {lines.map(l => (
                <tr key={l.id}>
                  <Td>{l.linetype || "—"}{l.issystem ? <span style={{ fontSize:11, color:"var(--color-text-secondary)" }}> (calculated)</span> : null}</Td>
                  <Td>{l.vendorname || "—"}</Td>
                  <Td>{l.invoicenumber || "—"}</Td>
                  <Td>{l.workdescription || "—"}</Td>
                  <TdNum>{fmt(l.amount)}</TdNum>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td colSpan={4} style={{ padding:"8px 12px", fontSize:12, fontWeight:600, color:B.cyanDark, textAlign:"right" }}>Total</td>
              <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(lines.reduce((s, l) => s + r2(l.amount), 0))}</TdNum>
            </tr></tfoot>
          </table>)}

      {/* ── Edit ── */}
      {editing && <>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead><tr><Th>Type</Th><Th>Vendor</Th><Th>Invoice #</Th><Th>Type of work</Th><Th>Amount</Th><Th></Th></tr></thead>
          <tbody>
            {editing.map((l, i) => l.issystem
              ? <tr key={l.id || i}>
                  <Td>{l.linetype} <span style={{ fontSize:11, color:"var(--color-text-secondary)" }}>(calculated)</span></Td>
                  <Td>—</Td><Td>—</Td><Td>{l.workdescription || "—"}</Td>
                  <TdNum>{fmt(l.amount)}</TdNum><Td />
                </tr>
              : <tr key={l.id || "new" + i}>
                  <Td style={{ minWidth:170 }}><FSelect value={l.linetype || ""} onChange={upd(i, "linetype")} options={opts(l.linetype)} /></Td>
                  <Td><FInput value={l.vendorname || ""} onChange={upd(i, "vendorname")} /></Td>
                  <Td style={{ maxWidth:120 }}><FInput value={l.invoicenumber || ""} onChange={upd(i, "invoicenumber")} /></Td>
                  <Td><FInput value={l.workdescription || ""} onChange={upd(i, "workdescription")} /></Td>
                  <Td style={{ maxWidth:140 }}><FInputNum value={l.amount} onChange={upd(i, "amount")} /></Td>
                  <Td><Btn variant="danger" onClick={() => delRow(i)}>×</Btn></Td>
                </tr>)}
          </tbody>
        </table>

        <div style={{ display:"flex", alignItems:"center", gap:16, marginTop:8, flexWrap:"wrap", fontSize:12 }}>
          <Btn onClick={addRow}>+ Add line</Btn>
          {editing.length === 0
            ? <span style={{ color:"var(--color-text-secondary)" }}>No lines. Saving removes the breakdown and sets the scheduled {title} to $0.</span>
            : <span>Lines: <b>{fmt(draftTotal)}</b></span>}
          <span>Scheduled: <b>{fmt(header)}</b></span>
          {diff === 0
            ? <span style={{ color:"#0F6E56", fontWeight:600 }}>Ties out</span>
            : !needsConfirm
              ? <span style={{ color:"#0F6E56", fontWeight:600 }}>Sets the scheduled {title} to {fmt(draftTotal)}</span>
              : <span style={{ color:"#A32D2D", fontWeight:600 }}>Off by {fmt(diff)}</span>}
        </div>
        {needsConfirm && (
          <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, marginTop:8, cursor:"pointer" }}>
            <input type="checkbox" checked={confirmTotal} onChange={e => setConfirmTotal(e.target.checked)} />
            Change the scheduled {title} from {fmt(header)} to {fmt(draftTotal)}
          </label>
        )}
        {err && <div style={{ color:"#993C1D", fontSize:12, marginTop:8 }}>{err}</div>}
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:10 }}>
          <Btn onClick={() => { setEditing(null); setErr(""); }} disabled={busy}>Cancel</Btn>
          <Btn variant="primary" onClick={save} disabled={busy}>{busy ? "Saving..." : "Save line items"}</Btn>
        </div>
      </>}
    </div>
  );
};
